import {
  gridColumnLookupSelector,
  gridColumnVisibilityModelSelector,
  isLeaf,
  useGridSelector
} from '@mui/x-data-grid-premium';
import { isEqual } from 'lodash';

const DEFAULT_COLUMNS_STATE = {
  tier: false,
  ssuStatus: false,
  ssuStatusDetail: false,
  dueDate: false,
  updatedAt: false,
  updatedBy: false,
  completedDateTime: false
};

const GRID_CHECKBOX_SELECTION_FIELD = '__check__';

export function extractFieldsWithGroupId(data, groupId = null) {
  return data.reduce((acc, item) => {
    if ('children' in item) {
      const rootGroupId = !groupId ? item.groupId : groupId;
      const extractedFields = extractFieldsWithGroupId(item.children, rootGroupId);

      extractedFields.forEach((value, key) => acc.set(key, value));
    }

    if ('field' in item) {
      acc.set(item.field, groupId);
    }

    return acc;
  }, new Map());
}

export const getColumnGroupLeaves = (group, callback) => {
  group.children.forEach(child => {
    if (isLeaf(child)) {
      callback(child.field);
    } else {
      getColumnGroupLeaves(child, callback);
    }
  });
};

export function getAllGroupedColumns(columnGroupingModel, columnLookup) {
  const extractedFieldsMap = columnGroupingModel?.length ? extractFieldsWithGroupId(columnGroupingModel) : new Map();

  return removeDuplicateGroups([
    ...Object.values(columnLookup).reduce((acc, column) => {
      if (!extractedFieldsMap.has(column.field)) {
        acc.push({ field: column.field });
      } else {
        acc.push(columnGroupingModel?.find(({ groupId }) => groupId === extractedFieldsMap.get(column.field)));
      }

      return acc;
    }, [])
  ]);
}

export function sortGroupChildren(groupedColumns, orderArray) {
  return groupedColumns.map(item => {
    if (item.children && Array.isArray(item.children)) {
      return {
        ...item,
        children: [...item.children].sort((a, b) => {
          const indexA = orderArray.indexOf(a.field);
          const indexB = orderArray.indexOf(b.field);

          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return 0;
        })
      };
    }
    return item;
  });
}

export function removeDuplicateGroups(items) {
  const seenGroupIds = new Set();
  const result = [];

  for (const item of items) {
    if ('groupId' in item && item.groupId) {
      if (!seenGroupIds.has(item.groupId)) {
        seenGroupIds.add(item.groupId);
        result.push(item);
      }
    } else {
      result.push(item);
    }
  }

  return result;
}

export function filterColumns(allGroupedColumns, columnLookup, search) {
  const searchUpper = search.toUpperCase();

  return allGroupedColumns?.reduce((filtered, group) => {
    if ('field' in group) {
      if (columnLookup[group.field]?.headerName?.toUpperCase().includes(searchUpper)) {
        filtered.push(group);
      }
    } else if (group?.children) {
      if (group.groupId.toUpperCase().includes(searchUpper)) {
        filtered.push(...group.children);
      } else {
        const filteredChildren = filterColumns(group.children, columnLookup, search);

        if (filteredChildren.length > 0) {
          filtered.push({
            ...group,
            children: filteredChildren
          });
        }
      }
    }

    return filtered;
  }, []);
}

export function useColumnPanelActions(apiRef, defaultState) {
  const columnVisibilityModel = useGridSelector(apiRef, gridColumnVisibilityModelSelector);
  const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);

  const columnVisibilityModelValues = Object.values(columnVisibilityModel);
  const columnLookupValues = Object.values(columnLookup);

  const isToggleAllChecked = columnVisibilityModelValues.every(column => column === true);
  const isToggleAllIndeterminate =
    !isToggleAllChecked &&
    ((columnVisibilityModelValues.length > 0 && columnVisibilityModelValues.length < columnLookupValues.length) ||
      columnVisibilityModelValues.some(value => value === true));
  const isResetDisabled = isEqual(defaultState ?? DEFAULT_COLUMNS_STATE, columnVisibilityModel);

  function toggleColumn(field, checked) {
    apiRef.current.setColumnVisibility(field, checked);
  }

  function toggleColumnGroup(checked, group) {
    const newColumnVisibilityModel = {
      ...columnVisibilityModel
    };

    getColumnGroupLeaves(group, field => {
      newColumnVisibilityModel[field] = checked;
    });

    apiRef.current.setColumnVisibilityModel(newColumnVisibilityModel);
  }

  function toggleAllColumns() {
    if (isToggleAllChecked) {
      hideAll();

      return;
    }

    showAll();
  }

  function resetToDefault(defaultState = DEFAULT_COLUMNS_STATE) {
    apiRef.current.restoreState({
      columns: {
        columnVisibilityModel: defaultState
      }
    });
  }

  function showAll() {
    apiRef.current.setColumnVisibilityModel(updateObjectValues(true, Object.keys(columnLookup)));
  }

  function hideAll() {
    apiRef.current.setColumnVisibilityModel(updateObjectValues(false, Object.keys(columnLookup)));
  }

  return {
    isToggleAllChecked,
    isToggleAllIndeterminate,
    isResetDisabled,
    toggleColumn,
    toggleColumnGroup,
    toggleAllColumns,
    resetToDefault
  };
}

function updateObjectValues(newValue, keysToUpdate) {
  return Object.fromEntries(
    keysToUpdate.map(key => (key === GRID_CHECKBOX_SELECTION_FIELD ? [key, true] : [key, newValue]))
  );
}

export function isChildrenHasValue(value, data, columnLookup) {
  if (!value) return true;

  const result = [];

  function extractChildrenValues(data) {
    data.forEach(data => {
      if ('field' in data) {
        result.push(columnLookup[data.field].headerName?.toUpperCase() ?? '');
      }

      if ('children' in data && data.children?.length) {
        result.push(data.groupId.toUpperCase());

        extractChildrenValues(data.children);
      }
    });
  }

  extractChildrenValues(data);

  return result.some(item => item.includes(value.toUpperCase()));
}
